import 'core-js/modules/es.array.iterator';
import Vue from 'vue';
import Router from 'vue-router';
import generalManifest from 'manifests/general.json';
import AuthRoutes from './auth/_routes';
import BlogRoutes from './blog/_routes';
import CompanyRoutes from './company/_routes';
import FeaturesRoutes from './features/_routes';
import LegalRoutes from './legal/_routes';
import PricingRoutes from './pricing/_routes';
import SearchRoutes from './search/_routes';
import SupportRoutes from './support/_routes';
import TemplateRoutes from './templates/_routes';
import { isServiceRequestError } from 'src/services/service-request';
import { convertAbsoluteUrlToRelative } from 'src/utilities/url-helper';
import { getActiveSession } from 'src/services/session';
import { reportReferral } from 'src/services/referral';

Vue.use(Router);

// If the URL starts with `//`, this causes an error in the browser when Vue router calls `replaceState`
// DOMException: Failed to execute 'replaceState' on 'History': A history state object with URL 'https:/...
// So, detect this and remove the duplicate leading `/` if needed
// NOTE: Loosely based on some code in VueRouter...
const protocolAndPath = window.location.protocol + '//' + window.location.host;
let absolutePath = window.location.href.replace(protocolAndPath, '');
if (/^\/\//.test(absolutePath)) {
	// Trim off the duplicate leading '/'
	absolutePath = absolutePath.substr(1);
	window.history.replaceState({}, '', protocolAndPath + absolutePath);
}

const router = new Router({
	mode: 'history',
	base: '/',
	scrollBehavior(to, from, savedPosition) {
		// When filtering template groupings via radio buttons, don't scroll
		const templateGroupings = ['industry', 'type', 'feature'];
		if (!to.hash && templateGroupings.includes(to.params.grouping) && to.name === 'template-index' && from.name === 'template-index') {//
			return false;
		}
		else if (!to.hash && from.name !== 'template-index' && to.matched.length > 1 && (to.matched[1].name || '') === 'template-with-tag-details') {
			if (document.querySelector('h1').getBoundingClientRect().top > -20)
				return false;
			return window.scrollTo({ top: 0, behavior: 'smooth' });
		}
		else if (to.hash) {
			if (to.hash === '#mobile-menu-placeholder') {
				history.replaceState('', document.title, window.location.pathname + window.location.search);
			}
			return {
				selector: to.hash
			};
		}
		else if	(savedPosition && !to.hash.length) {
			return false;
		}
		else
			return window.scrollTo({ top: 0, behavior: 'auto' });
	},
	routes: getLpRoutes().concat([
		{
			path: '/',
			name: 'home',
			component: () => import(/* webpackChunkName: 'Home' */ './Home.vue'),
			props: true
		},
		...AuthRoutes,
		...BlogRoutes,
		...CompanyRoutes,
		...FeaturesRoutes,
		...LegalRoutes,
		...PricingRoutes,
		...SupportRoutes,
		...SearchRoutes,
		...TemplateRoutes,
		{ path: '*', redirect: '/' }
	])
});

router.onReady(() => {
	if (!router.currentRoute.meta.auth && document.referrer !== location.href)
		reportReferral();
});

router.beforeEach((to, from, next) => {
	// Redirect to /login page for routes that require authentication if the user is not logged in
	// Reference: https://router.vuejs.org/guide/advanced/meta.html
	if (to.matched.some(record => record.meta.auth)) {
		// Attempt to validate or establish the user's session
		getActiveSession().then(session => {
			next();
		}).catch(() => {
			// If the user is not logged in and auth is required, then redirect to the login page
			if (to.matched.some(record => record.meta.auth == 'required'))
				next({
					path: '/login',
					query: { returnUrl: to.fullPath }
				});
			else
				next();
		});
	}
	else {
		next();
	}
});

// Handle errors that occur during router navigation
// Reference: https://router.vuejs.org/api/#router-onerror
router.onError(function handleRouteNavigationError(err) {
	// If a service request fails with a 401 (Unauthorized) error during navigation,
	// then this means that the user is not logged in, so redirect to the login page
	if (isServiceRequestError(err) && err.statusCode === 401) {
		// Return to the current page by default
		let returnUrl = location.pathname + location.search;
		// Since Vue router has already changed (reverted) the window location by the time the error
		// handler fires, use the URL of the page that made the service request that failed
		if (err.referrerUrl)
			returnUrl = convertAbsoluteUrlToRelative(err.referrerUrl);
		window.location.assign(`/login?returnurl=${encodeURI(returnUrl)}`);
	}
});

function getLpRoutes() {
	const lps = [];

	generalManifest.articles.filter(article => article.route.split('/')[1] === 'l' ).forEach(lp => {
		const kebabName = lp.route.replace('/l/', '');
		let pascalName = kebabName.replace(/([-_][a-z])/ig, ($1) => {
			return $1.toUpperCase()
				.replace('-', '')
				.replace('_', '')
				.substr(0, 1);
			});
		pascalName = pascalName.charAt(0).toUpperCase() + pascalName.slice(1);

		lps.push({
			path: lp.route,
			name: kebabName,
			component: () => import(/* webpackChunkName: 'LandingPage' */ `./landing/${pascalName}.vue`),
			props: true
		});
	});

	return lps;
}

export default router;

